import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { 
  Bell, 
  Mail, 
  MessageSquare, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Settings,
  Send,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null);
  const [preferences, setPreferences] = useState({
    email_enabled: true,
    sms_enabled: true,
    immediate_notifications: true,
    daily_digest: true,
    digest_time: '07:00'
  });
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, prefsRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications/status`),
        axios.get(`${API_URL}/api/notifications/preferences/${user?.id}`)
      ]);
      setStatus(statusRes.data);
      setPreferences(prefsRes.data);
      setTestEmail(user?.email || '');
      setTestPhone(user?.phone || '');
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/notifications/preferences/${user?.id}`, preferences);
      toast.success('Notification preferences saved');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API_URL}/api/notifications/test`, {
        email: testEmail || null,
        phone: testPhone || null
      });
      
      if (response.data.results.email_sent || response.data.results.sms_sent) {
        toast.success('Test notification sent successfully');
      } else {
        toast.warning('Notification not sent - check configuration');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Failed to send test notification');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header title="Notification Settings" />
        <div className="p-6 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="notification-settings-page">
      <Header title="Notification Settings" />

      <div className="p-6 space-y-6 max-w-4xl mx-auto">
        {/* System Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-slate-500" />
              System Configuration
            </CardTitle>
            <CardDescription>Current notification system status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Overall Status */}
              <div className="p-4 rounded-lg border bg-slate-50">
                <div className="flex items-center gap-2 mb-2">
                  <Bell className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium">Notifications</span>
                </div>
                {status?.notifications_enabled ? (
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Enabled
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                    <Clock className="w-3 h-3 mr-1" /> Pending Setup
                  </Badge>
                )}
              </div>

              {/* Email Status */}
              <div className="p-4 rounded-lg border bg-slate-50">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium">Email ({status?.email?.provider})</span>
                </div>
                {status?.email?.configured ? (
                  <div>
                    <Badge className="bg-green-100 text-green-700 border-green-200">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Configured
                    </Badge>
                    <p className="text-xs text-slate-500 mt-1">{status.email.sender_email}</p>
                  </div>
                ) : (
                  <Badge className="bg-slate-100 text-slate-600 border-slate-200">
                    <XCircle className="w-3 h-3 mr-1" /> Not Configured
                  </Badge>
                )}
              </div>

              {/* SMS Status */}
              <div className="p-4 rounded-lg border bg-slate-50">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium">SMS ({status?.sms?.provider})</span>
                </div>
                {status?.sms?.configured ? (
                  <div>
                    <Badge className="bg-green-100 text-green-700 border-green-200">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Configured
                    </Badge>
                    <p className="text-xs text-slate-500 mt-1">{status.sms.phone_number}</p>
                  </div>
                ) : (
                  <Badge className="bg-slate-100 text-slate-600 border-slate-200">
                    <XCircle className="w-3 h-3 mr-1" /> Not Configured
                  </Badge>
                )}
              </div>
            </div>

            {!status?.notifications_enabled && (
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Setup Required:</strong> To enable notifications, add your SendGrid and Twilio API keys to the backend .env file, then set NOTIFICATIONS_ENABLED=true.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-teal-600" />
              Your Notification Preferences
            </CardTitle>
            <CardDescription>Choose how you want to receive notifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Channels */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-700">Notification Channels</h3>
              
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-slate-500">Receive detailed notifications via email</p>
                  </div>
                </div>
                <Switch
                  checked={preferences.email_enabled}
                  onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, email_enabled: checked }))}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium">SMS Notifications</p>
                    <p className="text-sm text-slate-500">Receive quick alerts via text message</p>
                  </div>
                </div>
                <Switch
                  checked={preferences.sms_enabled}
                  onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, sms_enabled: checked }))}
                />
              </div>
            </div>

            {/* Timing */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-700">When to Notify</h3>
              
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <p className="font-medium">Immediate Notifications</p>
                  <p className="text-sm text-slate-500">Get notified immediately when new visits are scheduled</p>
                </div>
                <Switch
                  checked={preferences.immediate_notifications}
                  onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, immediate_notifications: checked }))}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="flex-1">
                  <p className="font-medium">Daily Digest</p>
                  <p className="text-sm text-slate-500">Receive a summary of your schedule each morning</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="digest-time" className="text-sm">Time:</Label>
                    <Input
                      id="digest-time"
                      type="time"
                      value={preferences.digest_time}
                      onChange={(e) => setPreferences(prev => ({ ...prev, digest_time: e.target.value }))}
                      className="w-28"
                      disabled={!preferences.daily_digest}
                    />
                  </div>
                  <Switch
                    checked={preferences.daily_digest}
                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, daily_digest: checked }))}
                  />
                </div>
              </div>
            </div>

            <Button 
              onClick={handleSavePreferences} 
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Preferences
            </Button>
          </CardContent>
        </Card>

        {/* Test Notification */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-purple-600" />
              Test Notification
            </CardTitle>
            <CardDescription>Send a test notification to verify your setup</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label>Test Email</Label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Test Phone (E.164 format)</Label>
                <Input
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="+447123456789"
                />
              </div>
            </div>
            <Button 
              onClick={handleTestNotification} 
              disabled={testing || (!testEmail && !testPhone)}
              variant="outline"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Test Notification
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
